import React from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import Modal from './Modal';

import { AlertTypes } from '../constants';
import { T, FONT_MONO, SRP_ALERTS } from '../constants/tokens';

/**
 * Rows need hover and focus, which an inline style cannot carry. Choosing an
 * alert is consequential enough that the row should visibly respond before
 * it is clicked.
 */
const useStyles = makeStyles({
  row: {
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': {
      background: T.surfaceSoft,
      borderColor: T.muted
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.focus,
      outlineOffset: 2
    },
    '&:disabled': {
      cursor: 'not-allowed',
      background: T.surfaceSoft,
      borderColor: T.hairline
    }
  }
});

/**
 * One choice in the SRP alert and drill pickers: the status's colour, its
 * name, what the badge shows, and what it asks staff to do.
 *
 * A row that cannot be chosen says why (`reason`) where the badge text would
 * be, so nothing is disabled without an explanation.
 *
 * @param {{ item: { status: object, label?: string, message?: string, action?: string, reason?: string }, onSelect: function }} props
 */
export function PickerRow({ item, onSelect }) {
  const classes = useStyles();
  const disabled = item.reason != null;

  return (
    <button
      type='button'
      onClick={() => onSelect(item.status)}
      disabled={disabled}
      className={classes.row}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        padding: 16,
        border: '1px solid ' + T.hairline,
        borderRadius: 6,
        background: T.elevated,
        cursor: 'pointer'
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 9999,
          flex: 'none',
          background: item.status.getColor().main,
          opacity: disabled ? 0.4 : 1,
          boxShadow: '0 0 0 1px rgba(35,37,29,0.12)'
        }}
      />
      <span style={{ flex: '1 1 160px', minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 16,
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '-0.32px',
            color: disabled ? T.muted : T.ink
          }}
        >
          {item.label ?? item.status.getDashboardStatus()}
        </span>
        {(disabled || item.message) &&
          <span style={{ display: 'block', fontFamily: FONT_MONO, fontSize: 12, color: T.body }}>
            {disabled ? item.reason : 'Badge LCD · ' + item.message}
          </span>}
      </span>
      <span
        style={{
          flex: '1 1 150px',
          minWidth: 0,
          textAlign: 'right',
          fontSize: 14,
          lineHeight: '20px',
          color: disabled ? T.muted : T.body,
          textWrap: 'pretty'
        }}
      >
        {item.action}
      </span>
    </button>
  );
}

/** Why a status cannot be set: its badges need the update. */
export const NEEDS_UPDATE = 'Needs the badge update';

/**
 * "SRP alert": the Standard Response Protocol alerts the dashboard can set
 * for the whole school, in the protocol's order.
 *
 * Medical and Admin are not offered. They come from badges.
 *
 * An alert the school's badges cannot show yet (`pending`, from the server)
 * is listed but cannot be chosen, with the reason, so the full protocol is
 * visible before every part of it can be used.
 */
export default function AlertPicker({ open, onClose, onSelect, pending = [] }) {
  const rows = Object.keys(SRP_ALERTS)
    .filter((key) => AlertTypes[key] != null)
    .map((key) => ({ key, status: AlertTypes[key], ...SRP_ALERTS[key] }))
    .map((item) => ({ ...item, reason: pending.includes(item.status.getDashboardStatus()) ? NEEDS_UPDATE : undefined }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      eyebrow='Standard response protocol'
      title='SRP alert'
      body='This alerts every badge in the school. Choose the response you want staff to take.'
      actions={<Button variant='outlined' onClick={onClose}>Cancel</Button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {rows.map((item) => <PickerRow key={item.key} item={item} onSelect={onSelect} />)}
      </div>

      {rows.some((item) => item.reason === NEEDS_UPDATE) &&
        <div style={{ fontSize: 13, lineHeight: '18px', color: T.body, marginBottom: 8 }}>
          Alerts marked as needing the badge update become available once this school&apos;s badges and hubs can show their colours.
        </div>}
    </Modal>
  );
}

PickerRow.propTypes = {
  item: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired
};

AlertPicker.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  /** Names of alert types the school's badges cannot show yet. */
  pending: PropTypes.arrayOf(PropTypes.string)
};
